import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { DashboardLayout } from '@/components/DashboardLayout';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';
import { useSupportWebSocket } from '@/hooks/useSupportWebSocket';
import { MessageAttachments, getFileIcon } from '@/components/MessageAttachments';
import { 
  MessageSquare, 
  Plus,
  Search, 
  RefreshCw, 
  Send, 
  Clock, 
  CheckCircle, 
  AlertCircle,
  XCircle,
  ArrowLeft,
  CheckCheck,
  Paperclip,
  X,
  Building2,
  Users,
  User,
  Wifi,
  WifiOff
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_CONFIG = {
  open: { label: 'Open', color: 'bg-blue-500' },
  in_progress: { label: 'In Progress', color: 'bg-yellow-500' },
  resolved: { label: 'Resolved', color: 'bg-green-500' },
  closed: { label: 'Closed', color: 'bg-slate-400' }
};

const PRIORITY_CONFIG = {
  low: { label: 'Low', color: 'bg-slate-100 text-slate-700' },
  medium: { label: 'Medium', color: 'bg-blue-100 text-blue-700' },
  high: { label: 'High', color: 'bg-orange-100 text-orange-700' },
  urgent: { label: 'Urgent', color: 'bg-red-100 text-red-700' }
};

// Role display helper
const ROLE_CONFIG = {
  reseller: { label: 'Synergy Partner', color: 'bg-blue-100 text-blue-700', icon: Building2 },
  sub_reseller: { label: 'Channel Partner', color: 'bg-cyan-100 text-cyan-700', icon: Users },
  client: { label: 'Client', color: 'bg-green-100 text-green-700', icon: User }
};

const getRoleBadge = (role) => {
  const config = ROLE_CONFIG[role] || { label: role, color: 'bg-slate-100 text-slate-700', icon: User };
  const Icon = config.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
      <Icon className="h-3 w-3" />
      {config.label}
    </span>
  );
};

export default function ResellerSupport() {
  const { user } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [replyContent, setReplyContent] = useState('');
  const [sendingReply, setSendingReply] = useState(false);
  const [showTicketList, setShowTicketList] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [newTicket, setNewTicket] = useState({ subject: '', content: '', priority: 'medium' });
  const [creating, setCreating] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [newTicketAttachments, setNewTicketAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);
  const newTicketFileInputRef = useRef(null);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  const fetchSummary = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/support/tickets/summary`);
      setSummary(response.data);
    } catch (error) {
      console.error('Failed to fetch summary');
    }
  }, []);

  const fetchTickets = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (searchQuery) params.append('search', searchQuery);
      
      const response = await axios.get(`${API}/support/tickets?${params.toString()}`);
      setTickets(response.data.tickets || []);
    } catch (error) {
      toast.error('Failed to fetch tickets');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, searchQuery]);

  const fetchTicketDetail = useCallback(async (ticketId) => {
    try {
      const response = await axios.get(`${API}/support/tickets/${ticketId}`);
      setSelectedTicket(response.data);
      setShowTicketList(false);
      setAttachments([]);
      setTimeout(() => inputRef.current?.focus(), 100);
    } catch (error) {
      toast.error('Failed to fetch ticket details');
    }
  }, []);

  // WebSocket message handler
  const handleWebSocketMessage = useCallback((message) => {
    if (message.type === 'new_message' && message.data?.message) {
      const msg = message.data.message;
      toast.info(`New message from ${msg.sender_name || msg.sender_email}`, {
        description: msg.content?.substring(0, 50) + '...'
      });
      
      if (selectedTicket?.id === message.data.ticket_id) {
        fetchTicketDetail(message.data.ticket_id);
      }
      fetchTickets();
      fetchSummary();
    } else if (message.type === 'ticket_updated') {
      if (selectedTicket?.id === message.data?.ticket_id) {
        fetchTicketDetail(message.data.ticket_id);
      }
      fetchTickets();
    }
  }, [selectedTicket?.id, fetchTicketDetail, fetchTickets, fetchSummary]);

  const { isConnected: wsConnected, subscribeToTicket, unsubscribeFromTicket } = useSupportWebSocket(handleWebSocketMessage);

  useEffect(() => {
    fetchSummary();
    fetchTickets();
  }, [fetchSummary, fetchTickets]);

  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  useEffect(() => {
    scrollToBottom();
  }, [selectedTicket?.messages, scrollToBottom]);

  useEffect(() => {
    if (selectedTicket?.id) {
      subscribeToTicket(selectedTicket.id);
    }
    return () => {
      if (selectedTicket?.id) {
        unsubscribeFromTicket(selectedTicket.id);
      }
    };
  }, [selectedTicket?.id, subscribeToTicket, unsubscribeFromTicket]);

  const handleFileUpload = async (files, isNewTicket = false) => {
    if (!files || files.length === 0) return;
    
    setUploading(true);
    const uploadedFiles = [];
    
    for (const file of files) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`${file.name} exceeds 10MB limit`);
        continue;
      }
      
      const formData = new FormData();
      formData.append('file', file);
      
      try {
        const response = await axios.post(`${API}/support/upload`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        uploadedFiles.push(response.data);
      } catch (error) {
        toast.error(`Failed to upload ${file.name}`);
      }
    }
    
    if (isNewTicket) {
      setNewTicketAttachments(prev => [...prev, ...uploadedFiles]);
    } else {
      setAttachments(prev => [...prev, ...uploadedFiles]);
    }
    
    setUploading(false);
  };

  const removeAttachment = (index, isNewTicket = false) => {
    if (isNewTicket) {
      setNewTicketAttachments(prev => prev.filter((_, i) => i !== index));
    } else {
      setAttachments(prev => prev.filter((_, i) => i !== index));
    }
  };

  const handleCreateTicket = async () => {
    if (!newTicket.subject.trim() || !newTicket.content.trim()) {
      toast.error('Please fill in subject and content');
      return;
    }

    setCreating(true);
    try {
      const formData = new FormData();
      formData.append('subject', newTicket.subject);
      formData.append('content', newTicket.content);
      formData.append('priority', newTicket.priority);
      formData.append('attachments', JSON.stringify(newTicketAttachments));
      
      await axios.post(`${API}/support/tickets/with-attachments`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Support ticket created successfully');
      setCreateOpen(false);
      setNewTicket({ subject: '', content: '', priority: 'medium' });
      setNewTicketAttachments([]);
      fetchTickets();
      fetchSummary();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create ticket');
    } finally {
      setCreating(false);
    }
  };

  const handleReply = async (e) => {
    e?.preventDefault();
    if (!replyContent.trim() && attachments.length === 0) return;

    setSendingReply(true);
    try {
      const formData = new FormData();
      formData.append('content', replyContent || '(Attachment)');
      formData.append('is_internal_note', 'false');
      formData.append('attachments', JSON.stringify(attachments));
      
      await axios.post(`${API}/support/tickets/${selectedTicket.id}/reply-with-attachments`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setReplyContent('');
      setAttachments([]);
      const response = await axios.get(`${API}/support/tickets/${selectedTicket.id}`);
      setSelectedTicket(response.data);
      fetchTickets();
      fetchSummary();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send reply');
    } finally {
      setSendingReply(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleReply();
    }
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return 'Today';
    } else if (date.toDateString() === yesterday.toDateString()) {
      return 'Yesterday';
    }
    return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const formatLastMessage = (dateString) => {
    const date = new Date(dateString);
    const today = new Date();
    
    if (date.toDateString() === today.toDateString()) {
      return formatTime(dateString);
    }
    return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
  };

  const getInitials = (email) => {
    return email?.substring(0, 2).toUpperCase() || 'UN';
  };

  const groupMessagesByDate = (messages) => {
    const groups = {};
    messages?.forEach(msg => {
      const date = formatDate(msg.created_at);
      if (!groups[date]) {
        groups[date] = [];
      }
      groups[date].push(msg);
    });
    return groups;
  };

  return (
    <DashboardLayout role={user?.role}>
      <div className="h-[calc(100vh-120px)] flex flex-col">
        {/* Header Stats */}
        <div className="flex items-center gap-2 sm:gap-4 mb-4 overflow-x-auto pb-2">
          <button
            onClick={() => setStatusFilter('all')}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm whitespace-nowrap transition-all ${
              statusFilter === 'all' ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            <MessageSquare className="h-4 w-4" />
            All ({summary.total || 0})
          </button>
          <button
            onClick={() => setStatusFilter('open')}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm whitespace-nowrap transition-all ${
              statusFilter === 'open' ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            <AlertCircle className="h-4 w-4" />
            Open ({summary.open || 0})
          </button>
          <button
            onClick={() => setStatusFilter('in_progress')}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm whitespace-nowrap transition-all ${
              statusFilter === 'in_progress' ? 'bg-yellow-500 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            <Clock className="h-4 w-4" />
            In Progress ({summary.in_progress || 0})
          </button>
          <Button 
            size="sm" 
            onClick={() => setCreateOpen(true)}
            className="ml-auto bg-green-500 hover:bg-green-600"
          >
            <Plus className="h-4 w-4 mr-1" />
            New
          </Button>
        </div>

        {/* Main Chat Container */}
        <div className="flex-1 flex bg-white rounded-xl shadow-sm border overflow-hidden">
          {/* Ticket List */}
          <div className={`w-full md:w-80 lg:w-96 border-r flex flex-col ${
            !showTicketList && selectedTicket ? 'hidden md:flex' : 'flex'
          }`}>
            <div className="p-3 border-b bg-slate-50">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Search tickets..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9 bg-white"
                />
              </div>
            </div>

            <ScrollArea className="flex-1">
              {loading ? (
                <div className="flex items-center justify-center h-32">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              ) : tickets.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-48 text-slate-400 p-4">
                  <MessageSquare className="h-12 w-12 mb-2 opacity-50" />
                  <p className="text-center">No tickets yet</p>
                  <Button 
                    size="sm" 
                    className="mt-4 bg-green-500 hover:bg-green-600"
                    onClick={() => setCreateOpen(true)}
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Create Ticket
                  </Button>
                </div>
              ) : (
                <div>
                  {tickets.map(ticket => (
                    <div
                      key={ticket.id}
                      data-testid={`ticket-${ticket.ticket_number}`}
                      onClick={() => fetchTicketDetail(ticket.id)}
                      className={`flex items-start gap-3 p-3 cursor-pointer hover:bg-slate-50 border-b transition-colors ${
                        selectedTicket?.id === ticket.id ? 'bg-blue-50 hover:bg-blue-50' : ''
                      }`}
                    >
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center text-white font-semibold flex-shrink-0 ${
                        ticket.status === 'open' ? 'bg-blue-500' :
                        ticket.status === 'in_progress' ? 'bg-yellow-500' :
                        ticket.status === 'resolved' ? 'bg-green-500' : 'bg-slate-400'
                      }`}>
                        {getInitials(ticket.owner_email)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-2 min-w-0">
                            <span className="font-semibold text-slate-800 truncate">
                              {ticket.owner_company || ticket.owner_email?.split('@')[0]}
                            </span>
                            {getRoleBadge(ticket.owner_role)}
                          </div>
                          <span className="text-xs text-slate-400 flex-shrink-0">
                            {formatLastMessage(ticket.updated_at || ticket.created_at)}
                          </span>
                        </div>
                        <p className="text-sm text-slate-600 truncate font-medium">{ticket.subject}</p>
                        <span className="text-xs text-slate-400 font-mono">{ticket.ticket_number}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
          </div>

          {/* Chat Area */}
          <div className={`flex-1 flex flex-col ${
            showTicketList && !selectedTicket ? 'hidden md:flex' : 'flex'
          }`}>
            {selectedTicket ? (
              <>
                {/* Chat Header */}
                <div className="h-16 px-4 flex items-center gap-3 border-b bg-slate-50">
                  <button
                    onClick={() => { setShowTicketList(true); setSelectedTicket(null); }}
                    className="md:hidden p-2 -ml-2 hover:bg-slate-200 rounded-lg"
                  >
                    <ArrowLeft className="h-5 w-5" />
                  </button>
                  
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold ${
                    selectedTicket.status === 'open' ? 'bg-blue-500' :
                    selectedTicket.status === 'in_progress' ? 'bg-yellow-500' :
                    selectedTicket.status === 'resolved' ? 'bg-green-500' : 'bg-slate-400'
                  }`}>
                    {getInitials(selectedTicket.owner_email)}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-semibold text-slate-800 truncate">{selectedTicket.owner_company || selectedTicket.subject}</h3>
                      {getRoleBadge(selectedTicket.owner_role)}
                    </div>
                    <div className="flex items-center gap-2 text-xs">
                      <span className="text-slate-500">{selectedTicket.owner_email}</span>
                      <span className="font-mono text-blue-600">{selectedTicket.ticket_number}</span>
                    </div>
                  </div>

                  <Badge className={`${STATUS_CONFIG[selectedTicket.status]?.color} text-white text-xs`}>
                    {STATUS_CONFIG[selectedTicket.status]?.label}
                  </Badge>
                </div>

                {/* Messages Area */}
                <div 
                  className="flex-1 overflow-y-auto p-4"
                  style={{ 
                    backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23e2e8f0' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
                    backgroundColor: '#f0f2f5'
                  }}
                >
                  {Object.entries(groupMessagesByDate(selectedTicket.messages)).map(([date, messages]) => (
                    <div key={date}>
                      <div className="flex justify-center my-4">
                        <span className="bg-white px-3 py-1 rounded-lg text-xs text-slate-500 shadow-sm">
                          {date}
                        </span>
                      </div>
                      {messages.filter(m => !m.is_internal_note).map((message, index) => {
                        const isInbound = message.is_inbound;
                        return (
                          <div
                            key={message.id || index}
                            className={`flex mb-2 ${isInbound ? 'justify-start' : 'justify-end'}`}
                          >
                            <div
                              className={`max-w-[80%] sm:max-w-[70%] rounded-lg px-3 py-2 shadow-sm ${
                                isInbound ? 'bg-white rounded-tl-none' : 'bg-green-100 rounded-tr-none'
                              }`}
                            >
                              {isInbound && (
                                <div className="text-xs font-semibold text-blue-600 mb-1">
                                  {message.sender_name || message.sender_email?.split('@')[0]}
                                </div>
                              )}
                              <p className="text-sm text-slate-800 whitespace-pre-wrap break-words">
                                {message.content}
                              </p>
                              
                              {/* Attachments */}
                              <MessageAttachments attachments={message.attachments} />
                              
                              <div className={`flex items-center gap-1 mt-1 ${isInbound ? 'justify-start' : 'justify-end'}`}>
                                <span className="text-[10px] text-slate-400">{formatTime(message.created_at)}</span>
                                {!isInbound && <CheckCheck className="h-3 w-3 text-blue-500" />}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ))}
                  <div ref={messagesEndRef} />
                </div>

                {/* Message Input */}
                {selectedTicket.status !== 'closed' ? (
                  <form onSubmit={handleReply} className="p-3 bg-slate-100 border-t">
                    {/* Attachment Preview */}
                    {attachments.length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-2 p-2 bg-white rounded-lg">
                        {attachments.map((att, i) => {
                          const AttIcon = getFileIcon(att.filename);
                          return (
                            <div key={att.filename + '-' + i} className="flex items-center gap-1 bg-slate-100 rounded-full px-2 py-1 text-xs">
                              <AttIcon className="h-3 w-3" />
                              <span className="truncate max-w-[80px]">{att.filename}</span>
                              <button type="button" onClick={() => removeAttachment(i)} className="text-red-500 hover:text-red-700">
                                <X className="h-3 w-3" />
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    )}
                    
                    <input
                      type="file"
                      ref={fileInputRef}
                      className="hidden"
                      multiple
                      onChange={(e) => handleFileUpload(e.target.files)}
                      data-testid="reply-file-input"
                    />
                    
                    <div className="flex items-end gap-2">
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploading}
                        className="w-10 h-10 rounded-full flex items-center justify-center bg-slate-200 hover:bg-slate-300 transition-colors"
                      >
                        {uploading ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Paperclip className="h-4 w-4 text-slate-600" />}
                      </button>
                      <div className="flex-1 bg-white rounded-full flex items-center px-4 py-2 shadow-sm">
                        <input
                          ref={inputRef}
                          type="text"
                          value={replyContent}
                          onChange={(e) => setReplyContent(e.target.value)}
                          onKeyPress={handleKeyPress}
                          placeholder="Type a message..."
                          className="flex-1 bg-transparent outline-none text-sm"
                          data-testid="reply-input"
                        />
                      </div>
                      <button
                        type="submit"
                        disabled={sendingReply || (!replyContent.trim() && attachments.length === 0)}
                        data-testid="send-button"
                        className={`w-12 h-12 rounded-full flex items-center justify-center transition-colors ${
                          (replyContent.trim() || attachments.length > 0)
                            ? 'bg-green-500 hover:bg-green-600 text-white'
                            : 'bg-slate-300 text-slate-500 cursor-not-allowed'
                        }`}
                      >
                        {sendingReply ? (
                          <RefreshCw className="h-5 w-5 animate-spin" />
                        ) : (
                          <Send className="h-5 w-5" />
                        )}
                      </button>
                    </div>
                  </form>
                ) : (
                  <div className="p-4 bg-slate-100 border-t text-center">
                    <p className="text-slate-500 text-sm">This ticket is closed</p>
                  </div>
                )}
              </>
            ) : (
              <div className="flex-1 flex flex-col items-center justify-center text-slate-400 bg-slate-50">
                <MessageSquare className="h-16 w-16 mb-4 opacity-30" />
                <h3 className="text-xl font-medium text-slate-600 mb-2">Support Chat</h3>
                <p className="text-sm text-center max-w-xs">
                  Select a conversation or create a new ticket to get started
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Create Ticket Dialog */}
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Create Support Ticket</DialogTitle>
              <DialogDescription>Fill in the details below to submit a support request.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Subject *</label>
                <Input
                  placeholder="Brief description of your issue"
                  value={newTicket.subject}
                  onChange={(e) => setNewTicket(prev => ({ ...prev, subject: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Priority</label>
                <Select 
                  value={newTicket.priority} 
                  onValueChange={(v) => setNewTicket(prev => ({ ...prev, priority: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(PRIORITY_CONFIG).map(([key, config]) => (
                      <SelectItem key={key} value={key}>{config.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Message *</label>
                <Textarea
                  placeholder="Describe your issue in detail..."
                  value={newTicket.content}
                  onChange={(e) => setNewTicket(prev => ({ ...prev, content: e.target.value }))}
                  rows={5}
                />
              </div>
              {/* Attachments */}
              <div className="space-y-2">
                <label className="text-sm font-medium">Attachments</label>
                <input
                  type="file"
                  ref={newTicketFileInputRef}
                  className="hidden"
                  multiple
                  onChange={(e) => handleFileUpload(e.target.files, true)}
                  data-testid="new-ticket-file-input"
                />
                <div className="flex flex-wrap gap-2">
                  {newTicketAttachments.map((att, i) => {
                    const AttIcon = getFileIcon(att.filename);
                    return (
                      <div key={att.filename + '-new-' + i} className="flex items-center gap-1 bg-slate-100 rounded-full px-2 py-1 text-xs">
                        <AttIcon className="h-3 w-3" />
                        <span className="truncate max-w-[100px]">{att.filename}</span>
                        <button onClick={() => removeAttachment(i, true)} className="text-red-500 hover:text-red-700">
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    );
                  })}
                  <button
                    type="button"
                    onClick={() => newTicketFileInputRef.current?.click()}
                    className="flex items-center gap-1 bg-slate-100 hover:bg-slate-200 rounded-full px-3 py-1 text-xs"
                    disabled={uploading}
                  >
                    {uploading ? <RefreshCw className="h-3 w-3 animate-spin" /> : <Paperclip className="h-3 w-3" />}
                    Add file
                  </button>
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
              <Button onClick={handleCreateTicket} disabled={creating} className="bg-green-500 hover:bg-green-600">
                {creating ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
                Submit Ticket
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
